// App version — build number is the total git commit count (auto-increments
// on every deploy), hash is the current short SHA. Both are injected at
// build time by vite.config.ts via `define`.
declare const __BUILD_NUMBER__: string;
declare const __BUILD_HASH__: string;
declare const __BUILD_DATE__: string;

export const APP_BUILD_NUMBER = typeof __BUILD_NUMBER__ !== 'undefined' ? __BUILD_NUMBER__ : '0';
export const APP_BUILD_HASH = typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : 'dev';
export const APP_BUILD_DATE = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : new Date().toISOString();
export const APP_VERSION = `2.0.${APP_BUILD_NUMBER}`;

// View types
export type ViewType = 'MESSAGE_FORM' | 'CHAT' | 'CONVERSATIONS' | 'KANBAN' | 'SETTINGS' | 'EVENTS' | 'GOOGLEDRIVE' | 'LOOKUP' |'CRM_CHAT' | 'STRIPE' | 'PHONE_TREE' | 'CHAT_AUTOMATION' | 'RECURRING_TASKS' | 'RECURRING_TASKS_PANEL';

// Event apps — TPA codes that are event/scheduling apps (not messaging)
export const EVENT_APPS = ['calendly', 'acuityscheduling'] as const;

/**
 * Server base URL that event-app webhook targets point to (routes to the
 * ulgebraGlobalWebhookRouter function via the /v1/webhooks hosting rewrite).
 * Keep it SHORT: Acuity rejects targets over 256 encoded chars (KAN-85).
 */
export const WEBHOOK_SERVER_BASE_URL = 'https://api.ulgebra.com/v1/webhooks';

/**
 * SaaS hosts that offer the "activity only" incoming target — the option that
 * logs an incoming event straight onto the CRM's own activity/event entity
 * instead of creating or matching a person/lead/deal record first.
 *
 * Keyed by SaaS module id, value is the dropdown label. Labels differ per host
 * because the underlying entity does: Pipedrive creates an Activity, Salesforce
 * an Event, Freshworks a meeting, Dynamics an appointment.
 *
 * THIS LIST IS DRIVEN BY THE BACKEND, not by what looks reasonable. A host
 * belongs here only if sms-functions.js has an
 * `ua_incoming_saas_target_module === "activity_only"` branch that early-returns
 * into a defined `addEventIn<Saas>History(...)`. Listing a host without that
 * branch gives the user a dropdown entry that silently does nothing.
 *
 * Deliberately absent:
 *   - activecampaign — has an activity_only branch, but it calls
 *                      addEventInActiveCampaignHistory, which is never defined.
 *                      Enabling it would drop the event at sync time.
 *   - zohodesk       — ticket-based; no addEventIn* handler exists to call.
 */
export const ACTIVITY_ONLY_SAAS: Record<string, string> = {
  pipedrive: 'Only activity',
  salesforce: 'Only Event',
  freshworkscrm: 'Only meetings',
  dynamics: 'Only appointment',
  zohocrm: 'Only Event',
  hubspotcrm: 'Only meeting',
  bitrix24: 'Only calendar event',
  odoo: 'Only Event',
  airtable: 'Only history row',
  zohobigin: 'Only Event',
  zohorecruit: 'Only Event',
  leadsquared: 'Only task',
};

/**
 * SaaS hosts where a cancelled or rescheduled booking leaves something behind to
 * dispose of, so the "when a meeting is cancelled" choice is meaningful.
 *
 * Mirrors the processStatusUpdateForSAAS_* handlers in sms-functions.js. The CRM
 * hosts act on a calendar event via mapObj.activity_id; the desk hosts act on the
 * ticket the booking raised, so "delete" removes the ticket and "mark_cancelled"
 * closes it with a comment rather than renaming anything.
 *
 * Slack and Monday are absent — Slack creates nothing to dispose of, and Monday
 * already marks its board item cancelled rather than deleting it, so the choice
 * would have no effect there.
 */
export const CANCELLABLE_EVENT_SAAS = [
  'zohocrm', 'pipedrive', 'hubspotcrm', 'salesforce',
  'dynamics', 'freshworkscrm', 'bitrix24',
  'zohodesk', 'freshdesk', 'zendesksupport',
] as const;

// Call apps — TPA codes that are call-centric (not messaging)
export const CALL_APPS = ['twiliocall'] as const;

// File / document apps
export const FILE_APPS = ['googledrive'] as const;

// Payment / invoice apps
export const INVOICE_APPS = ['stripe'] as const;

/**
 * Plural noun describing what the given TPA syncs into the SaaS — used in
 * settings titles like "Sync Twilio Messages to Zoho CRM" / "Sync Calendly
 * Events to Zoho CRM" / "Sync Stripe Invoices to HubSpot CRM".
 *
 * Defaults to "Activity" (singular kept here because "Activities" reads
 * awkwardly in the slot) for unknown TPAs.
 */
export function getTPASyncEntity(tpaCode: string | null | undefined): string {
  if (!tpaCode) return 'Activity';
  const code = tpaCode.toLowerCase();
  if ((EVENT_APPS as readonly string[]).includes(code)) return 'Events';
  if ((CALL_APPS as readonly string[]).includes(code)) return 'Calls';
  if ((FILE_APPS as readonly string[]).includes(code)) return 'Files';
  if ((INVOICE_APPS as readonly string[]).includes(code)) return 'Invoices';
  // Everything else in TPA_ALIASES is a messaging provider (SMS / WhatsApp /
  // RCS / etc.) — they all sync inbound messages.
  return 'Messages';
}

// TPA (Third Party App) aliases - maps short codes to full codes
export const TPA_ALIASES: Record<string, string> = {
  twilio: 'twilio',
  twiliocall: 'twiliocall',
  messagebird: 'messagebird',
  whatsapp: 'whatsapp',
  vonage: 'vonage',
  ringcentral: 'ringcentral',
  calendly: 'calendly',
  clickatell: 'clickatell',
  exotel: 'exotel',
  karix: 'karix',
  kaleyra: 'kaleyra',
  infobip: 'infobip',
  textlocal: 'textlocal',
  msg91: 'msg91',
  gupshup: 'gupshup',
  plivo: 'plivo',
  sinch: 'sinch',
  telnyx: 'telnyx',
  textmagic: 'textmagic',
  clicksend: 'clicksend',
  bulksms: 'bulksms',
  burstsms: 'burstsms',
  kudosity: 'burstsms',
  routemobile: 'routemobile',
  pinnacle: 'pinnacle',
  rivet: 'rivet',
  telebu: 'telebu',
  mtalkz: 'mtalkz',
  grapevine: 'grapevine',
  acuityscheduling: 'acuityscheduling',
  whatsappweb: 'whatsappweb',
  whatsappweb2: 'whatsappweb2',
  routemobilewa: 'routemobilewa',
  aiactions: 'aiactions',
  stripe: 'stripe',
  googledrive: 'googledrive',
  recurringtasks: 'recurringtasks',
  intercom: 'intercom',
  anysms: 'anysms',
  lookup: 'lookup',
};

// SaaS (CRM) aliases - maps short codes to full codes
export const SAAS_ALIASES: Record<string, string> = {
  zohocrm: 'zohocrm',
  hubspotcrm: 'hubspotcrm',
  pipedrive: 'pipedrive',
  bitrix24: 'bitrix24',
  salesforce: 'salesforce',
  salesforcecrm: 'salesforce',
  salesporcecrm: 'salesforce',
  freshworkscrm: 'freshworkscrm',
  freshdesk: 'freshdesk',
  zendesksell: 'zendesksell',
  zendesksupport: 'zendesksupport',
  // The live appCode is "twilioforhigh", so `high` IS the saas code — it is
  // deliberately not aliased to anything longer. "HighLevel" appears only as a
  // display label (see SAAS_PRETTY_NAMES).
  high: 'high',
  shopify: 'shopify',
  dynamics: 'dynamics',
  activecampaign: 'activecampaign',
  monday: 'monday',
  airtable: 'airtable',
  leadsquared: 'leadsquared',
  odoo: 'odoo',
  zohodesk: 'zohodesk',
  zohobooks: 'zohobooks',
  zohobigin: 'zohobigin',
  zohocampaigns: 'zohocampaigns',
  zohorecruit: 'zohorecruit',
  googlesheets: 'googlesheets',
  gspreadsheets: 'googlesheets',
  slack: 'slack',
  woocommerce: 'woocommerce',
  spreadsheet: 'spreadsheet',
  uaweb: 'uaweb',
};

// Firebase collection names
export const COLLECTIONS = {
  USERS: 'ulgebraUsers',
  APP_PREFERENCES: 'uaAppPreferences',
  USER_CREDITS: 'ualic_user_credits',
  USER_STATS: 'ualic_user_statistic_calendar_metric',
  LICENSE_OWNERS: 'UALicenseOwners',
  CUSTOMERS: 'customers',
  ERROR_LOGS: 'errorLogs',
  NOTIFICATION_DATA: 'rtDBNotificationIDVsDataMap',
} as const;

// Cloud Function endpoints
export const API_ENDPOINTS = {
  SERVICE_API: 'new_callServiceAuthorizedAPI',
  PROXY_REQUEST: 'requestByExternalService',
  PIPEDRIVE_CALL: 'makePipedriveHTTPCall',
  WEBHOOK_ROUTER: 'ulgebraGlobalWebhookRouter',
  HUBSPOT_API: 'hubspotApiHandler',
  AI_ACTION: 'runAIAction',
  RESOLVE_TRIAL_START: 'resolveTrialStart',
} as const;

// Default channels
export const CHANNELS = ['SMS', 'MMS', 'WHATSAPP', 'VOICE', 'EMAIL'] as const;

/**
 * TPAs that support the Inbox (conversations) view.
 * All other TPAs only show the compose/form view.
 * Matches the old app's hardcoded list in firebase-things.js.
 */
export const INBOX_SUPPORTED_TPAS = new Set([
  'twilio',
  'messagebird',
  'whatsapp',
  'twiliocall',
  'intercom',
  'ringcentral',
  'routemobilewa',
]);

/**
 * TPAs that support chat view (CRM record context) but should hide the
 * Inbox/Conversations tab, because the provider has no way to list
 * conversations — only single-contact history.
 *
 * Route Mobile used to be listed here on the belief that its conversation list
 * API had been withdrawn. It hadn't: the endpoint was renamed from
 * `fetch-conversation-numbers` to `fetch_conversation_numbers`, and the old
 * name's 404 was read as the API being gone.
 */
export const CHAT_ONLY_TPAS = new Set<string>([
  // Hidden BY PRODUCT CHOICE — the inbox works (list, threads, live ticks);
  // it is held back until Ulgebra releases it. Deleting this entry releases it.
  'routemobilewa',
]);

/**
 * TPAs that are NOT messaging providers — they have their own custom main view
 * instead of compose/inbox. These skip all messaging-related UI.
 */
export const NON_MESSAGING_TPAS = new Set([
  'googledrive',
  'lookup',
  'stripe',
  'recurringtasks',
]);

/**
 * True when the TPA sends/syncs messages (SMS, WhatsApp, RCS, …), so
 * per-message credit rates and inbox-driven cards (Inbox API, Connect
 * Automations) apply. False for events (Calendly/Acuity), calls, files
 * (Google Drive), invoices (Stripe), lookup and AI Actions.
 *
 * Not the same as NON_MESSAGING_TPAS above: that set only marks TPAs with a
 * custom main view (no compose/inbox). Event and call apps are NOT in that set
 * but still return false here because they never send messages.
 *
 * Unknown / not-yet-resolved codes return true so callers only hide
 * messaging-specific UI once they know the app is non-messaging.
 */
export function isMessageSendingTPA(tpaCode: string | null | undefined): boolean {
  if (!tpaCode) return true;
  const code = tpaCode.toLowerCase();
  // 'lookup' is read-only and 'aiactions' has no channels — neither has a
  // sync entity of its own, so getTPASyncEntity falls back to 'Messages'.
  if (code === 'lookup' || code === 'aiactions') return false;
  return getTPASyncEntity(code) === 'Messages';
}

/**
 * Views a non-messaging TPA can show; the first one is its default. A TPA not
 * listed here has a single view named after its code (googledrive → GOOGLEDRIVE).
 */
export const NON_MESSAGING_TPA_VIEWS: Partial<Record<string, ViewType[]>> = {
  recurringtasks: ['RECURRING_TASKS', 'RECURRING_TASKS_PANEL'],
};

/**
 * Pick the view for a non-messaging TPA. `force-ui-view` may use either
 * spelling (`recurring-tasks-panel` or `RECURRING_TASKS_PANEL`); anything the
 * TPA does not offer falls back to its default view.
 */
export function resolveNonMessagingView(tpaCode: string, forceView?: string | null): ViewType {
  const views = NON_MESSAGING_TPA_VIEWS[tpaCode] ?? [tpaCode.replace(/-/g, '_').toUpperCase() as ViewType];
  const wanted = (forceView || '').replace(/-/g, '_').toUpperCase() as ViewType;
  return views.includes(wanted) ? wanted : views[0];
}

/** Human-friendly display names for TPA codes (fallback when module is not loaded) */
const TPA_DISPLAY_NAMES: Record<string, string> = {
  googledrive: 'Google Drive',
  recurringtasks: 'Recurring Tasks',
  lookup: 'Ulgebra LookUp',
  intercom: 'Intercom',
  whatsapp: 'WhatsApp',
  twilio: 'Twilio',
  burstsms: 'Kudosity',
  kudosity: 'Kudosity',
  stripe: 'Stripe',
  twiliocall: 'Twilio Calls',
};

export function getTPADisplayName(tpaCode: string | null | undefined): string {
  if (!tpaCode) return 'Messaging Provider';
  return TPA_DISPLAY_NAMES[tpaCode.toLowerCase()] || tpaCode;
}

// Admin UIDs — mirrors the hardcoded list in saveLICUsersList Cloud Function
export const ADMIN_UIDS = new Set([
  'mKHY4LuTiDf53TYWBfKBeJkP91U2',
  'BaxP3oZnEKQXCQXOUiEIXXNc4G03',
  'TdkH4PXG0yc2iqGJgSMD2pOCOab2',
  'ieSDvIXnVqet9NSNl2A4nY0zf5y2',
  'mfkSZOFBABfIC3SPqeir22IewUG3',
  'VnhHx6o4buTAJ8H79RZTRzretVv1',
  '8B7CwRDRMlZsOhIGkK3uYi6rtSt1',
  'dWMN1KX1fgbXRYmtkRjelQ8VXqi1',
  '4Gf9BoaIqEXXdRtz2LgaduSOhOA3',
  'DawXYWBVZVcavMM8K4uaMkz5SC83',
  'q0hDGYGDNjOn98oBHiYlGVusxYB3',
]);

// Extension name patterns. First group is non-greedy so `pinnacleforsalesforce`
// splits at the FIRST "for" → pinnacle + salesforce. A greedy first group
// locks onto the last "for" inside `salesforce` and yields the wrong split
// (`pinnacleforsales` + `ce`).
export const EXTENSION_NAME_PATTERN = /^(\w+?)for(\w+)$/;

/** Apps excluded from licensing/trial checks — free or have separate billing */
export const LICENSING_EXCLUDED_APPS = new Set([
  'pinnacle',
  'routemobile',
  'routemobilewa',
  'telebu',
  'karix',
  'rivet',
  'telnyx',
  'whatcetra',
]);

/**
 * SaaS category labels.
 * Maps each resolved SaaS code to its correct software category
 * so the UI displays "Help Desk" instead of "CRM" for Zoho Desk, etc.
 */
export const SAAS_CATEGORY: Record<string, string> = {
  // CRM / Sales
  zohocrm: 'CRM',
  hubspotcrm: 'CRM',
  pipedrive: 'CRM',
  salesforce: 'CRM',
  dynamics: 'CRM',
  bitrix24: 'CRM',
  freshworkscrm: 'CRM',
  activecampaign: 'CRM',
  zohobigin: 'CRM',
  leadsquared: 'CRM',
  zendesksell: 'CRM',
  high: 'CRM',

  // Help Desk / Customer Support
  zohodesk: 'Help Desk',
  freshdesk: 'Help Desk',
  zendesksupport: 'Help Desk',

  // Other product types
  zohobooks: 'Accounting',
  zohocampaigns: 'Email Marketing',
  zohorecruit: 'Recruitment',
  shopify: 'E-commerce',
  woocommerce: 'WooCommerce',
  monday: 'Project Management',
  airtable: 'Database',
  googlesheets: 'Spreadsheet',
  slack: 'Messaging',
  odoo: 'ERP',
  spreadsheet: 'Spreadsheet',
  uaweb: 'Web',
};

/**
 * Return the user-facing category label for the given SaaS code.
 * Falls back to 'CRM' for unknown codes (most integrations are CRM-like).
 */
export function getSaaSCategory(saasCode: string | null | undefined): string {
  if (!saasCode) return 'CRM';
  return SAAS_CATEGORY[saasCode] || 'CRM';
}

/**
 * SaaS pretty names.
 * Maps each resolved SaaS code to its user-facing display name.
 */
export const SAAS_PRETTY_NAMES: Record<string, string> = {
  zohocrm: 'Zoho CRM',
  hubspotcrm: 'HubSpot CRM',
  pipedrive: 'Pipedrive',
  salesforce: 'Salesforce',
  dynamics: 'Microsoft Dynamics 365',
  bitrix24: 'Bitrix24',
  freshworks: 'Freshworks CRM',
  freshworkscrm: 'Freshworks CRM',
  activecampaign: 'ActiveCampaign',
  zohobigin: 'Zoho Bigin',
  'zoho-bigin': 'Zoho Bigin',
  leadsquared: 'LeadSquared',
  zendesksell: 'Zendesk Sell',
  'zendesk-sell': 'Zendesk Sell',
  high: 'HighLevel',
  zohodesk: 'Zoho Desk',
  'zoho-desk': 'Zoho Desk',
  freshdesk: 'Freshdesk',
  zendesksupport: 'Zendesk Support',
  'zendesk-support': 'Zendesk Support',
  zohobooks: 'Zoho Books',
  'zoho-books': 'Zoho Books',
  zohocampaigns: 'Zoho Campaigns',
  'zoho-campaigns': 'Zoho Campaigns',
  zohorecruit: 'Zoho Recruit',
  'zoho-recruit': 'Zoho Recruit',
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
  monday: 'Monday.com',
  airtable: 'Airtable',
  googlesheets: 'Google Sheets',
  'google-sheets': 'Google Sheets',
  slack: 'Slack',
  odoo: 'Odoo',
  spreadsheet: 'Spreadsheet',
  'ua-web': 'UA Web',
  uaweb: 'UA Web',
};

/**
 * Return the user-facing pretty name for the given SaaS code.
 * Falls back to the category label for unknown codes.
 */
export function getSaaSPrettyName(saasCode: string | null | undefined): string {
  if (!saasCode) return 'CRM';
  return SAAS_PRETTY_NAMES[saasCode] || getSaaSCategory(saasCode);
}

// Marketplace categories
export const MARKETPLACE_CATEGORIES = {
  CRM: 'CRM',
  HELPDESK: 'Helpdesk',
  ECOMMERCE: 'E-Commerce',
  MARKETING: 'Marketing',
  SCHEDULING: 'Scheduling',
  SPREADSHEET: 'Spreadsheet',
  COMMUNICATION: 'Communication',
} as const;
